/**
 * Client Hiring — Agency cockpit.
 *
 * For staffing-agency tenants like Cal Scientific. Lets recruiters:
 *  • Onboard client accounts (J&J, Pfizer, …) and invite their users
 *  • Create requirements on behalf of clients
 *  • Submit candidates to clients with optional PII redaction
 *  • Track every requirement's funnel and decision activity
 *
 * Lives at `/app/client-hiring`. Only visible when the tenant has the
 * `client_hiring` module entitlement.
 */
import { useEffect, useMemo, useState } from "react";
import AppShell from "../components/AppShell";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { Buildings, UserPlus, Briefcase, Eye, EyeSlash, Plus, Copy, Link as LinkIcon, ListBullets, DotsThree, PencilSimple, Key, ArrowsClockwise, Prohibit } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

const errMsg = (e) => formatApiError(e?.response?.data?.detail) || "Something went wrong";

const STAGE_LABELS = {
  submitted: "Submitted",
  client_reviewing: "Client reviewing",
  client_interview: "Client interview",
  client_decision: "Client deciding",
  selected: "Selected",
  rejected: "Rejected",
  withdrawn: "Withdrawn",
};

const STAGE_TONE = {
  submitted: "bg-indigo-100 text-indigo-800",
  client_reviewing: "bg-amber-100 text-amber-800",
  client_interview: "bg-violet-100 text-violet-800",
  selected: "bg-emerald-100 text-emerald-800",
  rejected: "bg-rose-100 text-rose-800",
  withdrawn: "bg-zinc-200 text-zinc-700",
};

export default function ClientHiring() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [selected, setSelected] = useState(null);     // open account detail panel
  const [createOpen, setCreateOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/client-hiring/client-accounts");
      setAccounts(data || []);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <AppShell>
      <div className="space-y-6" data-testid="client-hiring-page">
        <header className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold text-zinc-900">Client Hiring</h1>
            <p className="text-sm text-zinc-600 mt-1">
              Manage staffing engagements with external clients — onboard them,
              capture role briefs, submit candidates, give them their own portal.
            </p>
          </div>
          <Button onClick={() => setCreateOpen(true)} data-testid="ch-new-client">
            <Plus size={16} className="mr-1" /> Onboard new client
          </Button>
        </header>

        {/* KPI bar */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <KPI label="Active clients" value={accounts.filter(a => a.status === "active").length} tone="indigo" testid="ch-kpi-clients" />
          <KPI label="Open requirements" value={accounts.reduce((s, a) => s + (a.open_requirements || 0), 0)} tone="amber" testid="ch-kpi-open-reqs" />
          <KPI label="Active submissions" value={accounts.reduce((s, a) => s + (a.active_submissions || 0), 0)} tone="violet" testid="ch-kpi-active-subs" />
          <KPI label="Client users" value={accounts.reduce((s, a) => s + (a.client_user_count || 0), 0)} tone="zinc" testid="ch-kpi-users" />
        </div>

        {/* Client list */}
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-zinc-600 text-xs uppercase tracking-wide">
              <tr>
                <th className="px-4 py-3 text-left">Client</th>
                <th className="px-4 py-3 text-left">Primary contact</th>
                <th className="px-4 py-3 text-left">Open reqs</th>
                <th className="px-4 py-3 text-left">In review</th>
                <th className="px-4 py-3 text-left">Users</th>
                <th className="px-4 py-3 text-left">Blind</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-zinc-500">Loading…</td></tr>
              )}
              {!loading && accounts.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-zinc-500">
                  No client accounts yet — click <strong>Onboard new client</strong> to add your first one.
                </td></tr>
              )}
              {accounts.map((a) => (
                <tr key={a.id} className="border-t hover:bg-zinc-50 cursor-pointer" onClick={() => setSelected(a)} data-testid={`ch-acc-${a.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {a.logo_url
                        ? <img src={a.logo_url} alt={a.name} className="w-7 h-7 rounded object-cover" />
                        : <div className="w-7 h-7 rounded flex items-center justify-center text-white text-xs font-bold" style={{ background: a.brand_color || "#4f46e5" }}>
                            {(a.name || "?").slice(0,1).toUpperCase()}
                          </div>}
                      <div>
                        <div className="font-medium text-zinc-900">{a.name}</div>
                        <div className="text-xs text-zinc-500">/client/login/{a.slug}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-zinc-700">
                    {a.primary_contact_name || a.primary_contact_email || "—"}
                  </td>
                  <td className="px-4 py-3"><Badge variant="outline">{a.open_requirements}</Badge></td>
                  <td className="px-4 py-3"><Badge variant="outline">{a.active_submissions}</Badge></td>
                  <td className="px-4 py-3"><Badge variant="outline">{a.client_user_count}</Badge></td>
                  <td className="px-4 py-3">{a.default_blind_submit ? <EyeSlash size={16} className="text-zinc-500" /> : <Eye size={16} className="text-zinc-500" />}</td>
                  <td className="px-4 py-3"><Badge className={a.status === "active" ? "bg-emerald-100 text-emerald-800" : "bg-zinc-200 text-zinc-700"}>{a.status}</Badge></td>
                  <td className="px-4 py-3 text-right">
                    <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); setSelected(a); }} data-testid={`ch-open-${a.id}`}>
                      Open
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>

      {createOpen && <NewClientDialog onClose={() => setCreateOpen(false)} onCreated={() => { setCreateOpen(false); load(); }} />}
      {selected && <ClientDetail acc={selected} onClose={() => setSelected(null)} onChanged={load} />}
    </AppShell>
  );
}

function KPI({ label, value, tone = "zinc", testid }) {
  const palette = {
    indigo: "bg-indigo-50 border-indigo-200 text-indigo-900",
    amber:  "bg-amber-50 border-amber-200 text-amber-900",
    violet: "bg-violet-50 border-violet-200 text-violet-900",
    zinc:   "bg-white border-zinc-200 text-zinc-900",
  }[tone];
  return (
    <Card className={`p-4 ${palette}`} data-testid={testid}>
      <div className="text-xs uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-3xl font-bold mt-1">{value}</div>
    </Card>
  );
}

function NewClientDialog({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [blind, setBlind] = useState(true);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (name.trim().length < 2) return toast.error("Name required.");
    setSaving(true);
    try {
      await api.post("/client-hiring/client-accounts", {
        name: name.trim(),
        primary_contact_name: contactName || undefined,
        primary_contact_email: contactEmail || undefined,
        default_blind_submit: blind,
      });
      toast.success(`${name} onboarded.`);
      onCreated();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="ch-new-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Buildings size={20} className="text-indigo-600" /> Onboard a new client</DialogTitle>
          <DialogDescription>
            Each client gets its own branded portal, isolated requirements and PII rules.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Client name *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Johnson & Johnson" data-testid="ch-new-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Primary contact name</Label>
              <Input value={contactName} onChange={(e) => setContactName(e.target.value)} data-testid="ch-new-contact-name" />
            </div>
            <div>
              <Label className="text-xs">Primary contact email</Label>
              <Input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} data-testid="ch-new-contact-email" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={blind} onChange={(e) => setBlind(e.target.checked)} data-testid="ch-new-blind" />
            <span>Blind submission by default (recommended for bias-conscious clients)</span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="ch-new-save">{saving ? "Saving…" : "Onboard"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ClientDetail({ acc, onClose, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [requirements, setRequirements] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [reqOpen, setReqOpen] = useState(false);

  const load = async () => {
    try {
      const [a, r] = await Promise.all([
        api.get(`/client-hiring/client-accounts/${acc.id}`),
        api.get(`/client-hiring/requirements?client_account_id=${acc.id}`),
      ]);
      setDetail(a.data);
      setRequirements(r.data || []);
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [acc.id]);

  const copyPortalLink = async () => {
    const url = `${window.location.origin}/client/login/${acc.slug}`;
    try { await navigator.clipboard.writeText(url); toast.success("Portal link copied"); }
    catch { toast.error("Copy failed — long-press to copy manually"); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="ch-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            {acc.logo_url
              ? <img src={acc.logo_url} alt="" className="w-9 h-9 rounded object-cover" />
              : <div className="w-9 h-9 rounded flex items-center justify-center text-white font-bold" style={{ background: acc.brand_color || "#4f46e5" }}>{acc.name?.slice(0,1)}</div>}
            <div>
              <div className="text-xl font-semibold">{acc.name}</div>
              <div className="text-xs text-zinc-500 font-normal flex items-center gap-2">
                Portal · /client/login/{acc.slug}
                <button onClick={copyPortalLink} className="text-indigo-600 hover:underline inline-flex items-center gap-1" data-testid="ch-copy-portal">
                  <Copy size={12} /> copy
                </button>
              </div>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 mt-3">
          {/* Users */}
          <Card className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold flex items-center gap-2"><UserPlus size={18} className="text-indigo-600" /> Client users</h3>
              <Button size="sm" onClick={() => setInviteOpen(true)} data-testid="ch-invite-btn"><Plus size={14} className="mr-1" /> Invite user</Button>
            </div>
            <ul className="space-y-1">
              {(detail?.users || []).filter(u => u.is_active).map((u) => (
                <ClientUserRow key={u.id} u={u} accId={acc.id} onChanged={load} />
              ))}
              {(detail?.users || []).filter(u => u.is_active).length === 0 && (
                <li className="text-sm text-zinc-500 py-3 text-center">No users yet — invite the first hiring manager.</li>
              )}
            </ul>
          </Card>

          {/* Requirements */}
          <Card className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold flex items-center gap-2"><Briefcase size={18} className="text-indigo-600" /> Requirements</h3>
              <Button size="sm" onClick={() => setReqOpen(true)} data-testid="ch-newreq-btn"><Plus size={14} className="mr-1" /> New requirement</Button>
            </div>
            {requirements.length === 0 && (
              <div className="text-sm text-zinc-500 py-4 text-center">No requirements yet for {acc.name}.</div>
            )}
            <ul className="space-y-2">
              {requirements.map((r) => <RequirementRow key={r.id} req={r} clientAccount={detail || acc} onChanged={() => { load(); onChanged?.(); }} />)}
            </ul>
          </Card>
        </div>

        {inviteOpen && <InviteUserDialog accId={acc.id} onClose={() => setInviteOpen(false)} onInvited={() => { setInviteOpen(false); load(); }} />}
        {reqOpen && <NewRequirementDialog accId={acc.id} onClose={() => setReqOpen(false)} onCreated={() => { setReqOpen(false); load(); }} />}
      </DialogContent>
    </Dialog>
  );
}

function InviteUserDialog({ accId, onClose, onInvited }) {
  const [email, setEmail] = useState("");
  const [name,  setName]  = useState("");
  const [role,  setRole]  = useState("client_hiring_manager");
  const [busy,  setBusy]  = useState(false);

  const submit = async () => {
    if (!email || !name) return toast.error("Email and name required");
    setBusy(true);
    try {
      const { data } = await api.post(`/client-hiring/client-accounts/${accId}/invite`, { email, name, role });
      toast.success(`Invited ${name}. Setup link: ${data.setup_url}`);
      onInvited();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="ch-invite-dialog">
        <DialogHeader>
          <DialogTitle>Invite client user</DialogTitle>
          <DialogDescription>They&apos;ll receive a setup email with a magic link to set their password.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Email *</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="ch-invite-email" />
          </div>
          <div>
            <Label className="text-xs">Name *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="ch-invite-name" />
          </div>
          <div>
            <Label className="text-xs">Role</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="ch-invite-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="client_admin">Client Admin (sees all requirements + users)</SelectItem>
                <SelectItem value="client_hiring_manager">Hiring Manager (sees & decides on candidates)</SelectItem>
                <SelectItem value="client_reviewer">Reviewer (read-only, sees candidates)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="ch-invite-save">{busy ? "Inviting…" : "Send invite"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewRequirementDialog({ accId, onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [skills, setSkills] = useState("");
  const [location, setLocation] = useState("");
  const [headcount, setHeadcount] = useState(1);
  const [priority, setPriority] = useState("medium");
  const [summary, setSummary] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (title.trim().length < 2) return toast.error("Title required");
    setSaving(true);
    try {
      await api.post(`/client-hiring/requirements`, {
        client_account_id: accId,
        title: title.trim(),
        role_summary_md: summary || undefined,
        skills: skills.split(",").map(s => s.trim()).filter(Boolean),
        location: location || undefined,
        headcount,
        priority,
      });
      toast.success("Requirement opened.");
      onCreated();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl" data-testid="ch-newreq-dialog">
        <DialogHeader><DialogTitle>New requirement</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Role title *</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Clinical Engineer" data-testid="ch-req-title" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Label className="text-xs">Skills (comma-separated)</Label>
              <Input value={skills} onChange={(e) => setSkills(e.target.value)} placeholder="GMP, validation, calibration" data-testid="ch-req-skills" />
            </div>
            <div>
              <Label className="text-xs">Headcount</Label>
              <Input type="number" min={1} value={headcount} onChange={(e) => setHeadcount(parseInt(e.target.value || "1", 10))} data-testid="ch-req-headcount" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Location</Label>
              <Input value={location} onChange={(e) => setLocation(e.target.value)} data-testid="ch-req-location" />
            </div>
            <div>
              <Label className="text-xs">Priority</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger data-testid="ch-req-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Summary / JD (markdown supported)</Label>
            <Textarea rows={4} value={summary} onChange={(e) => setSummary(e.target.value)} data-testid="ch-req-summary" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="ch-req-save">{saving ? "Saving…" : "Open requirement"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RequirementRow({ req, clientAccount, onChanged }) {
  const [open, setOpen] = useState(false);
  const funnel = req.funnel || {};

  return (
    <li className="border border-zinc-200 rounded p-3" data-testid={`ch-req-row-${req.id}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="font-medium text-zinc-900">{req.title} <span className="text-zinc-400 text-xs">{req.code}</span></div>
          <div className="text-xs text-zinc-500 flex items-center gap-2 mt-1">
            <span>{req.location || "Anywhere"}</span> ·
            <span>HC {req.headcount}</span> ·
            <Badge variant="outline" className="text-[10px]">{req.priority}</Badge>
            <Badge className={req.status === "open" ? "bg-emerald-100 text-emerald-800 text-[10px]" : "bg-zinc-200 text-zinc-700 text-[10px]"}>{req.status}</Badge>
          </div>
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          {["submitted","client_reviewing","client_interview","selected","rejected"].map((s) => (
            <span key={s} className={`text-[10px] px-2 py-0.5 rounded ${STAGE_TONE[s] || "bg-zinc-100 text-zinc-700"}`}>
              {STAGE_LABELS[s]}: <strong>{funnel[s] || 0}</strong>
            </span>
          ))}
          <Button size="sm" variant="outline" onClick={() => setOpen(true)} className="ml-1" data-testid={`ch-req-open-${req.id}`}>
            <ListBullets size={12} className="mr-1" /> Submissions
          </Button>
        </div>
      </div>
      {open && <RequirementSubmissions req={req} clientAccount={clientAccount} onClose={() => { setOpen(false); onChanged?.(); }} />}
    </li>
  );
}

function RequirementSubmissions({ req, clientAccount, onClose }) {
  const [submissions, setSubmissions] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [pick, setPick] = useState([]);
  const [agencyNotes, setAgencyNotes] = useState("");
  const [activity, setActivity] = useState([]);

  const load = async () => {
    try {
      const [d, a] = await Promise.all([
        api.get(`/client-hiring/requirements/${req.id}`),
        api.get(`/client-hiring/requirements/${req.id}/activity`),
      ]);
      setSubmissions(d.data?.submissions || []);
      setActivity(a.data || []);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const loadCandidates = async () => {
    try {
      const { data } = await api.get("/candidates");
      const subIds = new Set(submissions.map(s => s.candidate_id));
      setCandidates((data || []).filter(c => !subIds.has(c.id)));
    } catch (e) {
      console.warn("candidates fetch failed", e?.message);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [req.id]);
  useEffect(() => { if (submissions !== null) loadCandidates(); /* eslint-disable-next-line */ }, [submissions]);

  const togglePick = (id) => setPick((p) => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const submit = async () => {
    if (pick.length === 0) return toast.error("Pick at least one candidate to submit.");
    try {
      await api.post(`/client-hiring/requirements/${req.id}/submit`, {
        candidate_ids: pick,
        agency_notes: agencyNotes || undefined,
      });
      toast.success(`${pick.length} candidate(s) submitted to ${clientAccount.name}.`);
      setPick([]); setAgencyNotes("");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="ch-subs-dialog">
        <DialogHeader>
          <DialogTitle>{req.title} <span className="text-zinc-400 text-sm">{req.code}</span></DialogTitle>
          <DialogDescription>Submissions to <strong>{clientAccount.name}</strong> and full activity stream.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
          <div className="md:col-span-2 space-y-4">
            {/* Submit section */}
            <Card className="p-4">
              <h4 className="font-semibold text-zinc-900 mb-2">Submit candidates</h4>
              {candidates.length === 0 ? (
                <p className="text-xs text-zinc-500">No more available candidates to submit. Add candidates in the Recruitment module first.</p>
              ) : (
                <>
                  <div className="max-h-48 overflow-y-auto border border-zinc-200 rounded mb-2">
                    {candidates.slice(0, 30).map((c) => (
                      <label key={c.id} className="flex items-center gap-2 p-2 text-sm hover:bg-zinc-50 cursor-pointer">
                        <input type="checkbox" checked={pick.includes(c.id)} onChange={() => togglePick(c.id)} data-testid={`ch-pick-${c.id}`} />
                        <span className="font-medium text-zinc-900">{c.name}</span>
                        <span className="text-xs text-zinc-500">{c.current_company || ""}</span>
                      </label>
                    ))}
                  </div>
                  <Label className="text-xs">Optional pitch / notes for the client</Label>
                  <Textarea rows={2} value={agencyNotes} onChange={(e) => setAgencyNotes(e.target.value)} data-testid="ch-pitch" />
                  <Button onClick={submit} size="sm" className="mt-2" disabled={pick.length === 0} data-testid="ch-submit-btn">
                    Submit {pick.length || ""} to client
                  </Button>
                </>
              )}
            </Card>

            {/* Submissions list */}
            <Card className="p-4">
              <h4 className="font-semibold text-zinc-900 mb-2">Submissions ({submissions.length})</h4>
              {submissions.length === 0 && <p className="text-xs text-zinc-500">No submissions yet.</p>}
              <ul className="space-y-1 max-h-72 overflow-y-auto">
                {submissions.map((s) => (
                  <li key={s.id} className="flex items-center justify-between text-sm py-1 border-b last:border-0 border-zinc-100">
                    <div>
                      <span className="text-zinc-900">{s.candidate_snapshot?.name}</span>
                      <span className="text-xs text-zinc-500 ml-2">{s.candidate_snapshot?.current_company || ""}</span>
                    </div>
                    <Badge className={`text-[10px] ${STAGE_TONE[s.stage] || "bg-zinc-200 text-zinc-700"}`}>{STAGE_LABELS[s.stage]}</Badge>
                  </li>
                ))}
              </ul>
            </Card>
          </div>

          {/* Activity */}
          <Card className="p-4">
            <h4 className="font-semibold text-zinc-900 mb-2">Activity</h4>
            <ul className="space-y-2 text-xs max-h-96 overflow-y-auto">
              {activity.map((e) => (
                <li key={e.id} className="border-l-2 border-indigo-200 pl-3">
                  <div className="text-zinc-900">{e.event_type.replaceAll(".", " · ")}</div>
                  <div className="text-zinc-500">
                    {e.actor_name || "system"} · {e.actor_side} · {new Date(e.at).toLocaleString()}
                  </div>
                </li>
              ))}
              {activity.length === 0 && <li className="text-zinc-500">No activity yet.</li>}
            </ul>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ---------------------------------------------------------------------------
// ClientUserRow — row + kebab menu (Edit, Resend invite, Set password,
// Reset password, Deactivate). Tenant + client_account scoped end-to-end.
// ---------------------------------------------------------------------------
function ClientUserRow({ u, accId, onChanged }) {
  const [menu, setMenu] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [busy, setBusy] = useState(false);

  const close = () => { setMenu(false); setDialog(null); };

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); onChanged(); close(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const onResend = () => act(
    async () => {
      const r = await api.post(`/api/client-hiring/client-accounts/${accId}/users/${u.id}/resend-invite`);
      if (r.data?.setup_url) navigator.clipboard?.writeText(r.data.setup_url).catch(() => {});
    },
    `Invite re-sent to ${u.email}. Setup link copied to clipboard.`,
  );
  const onReset = () => act(
    async () => {
      const r = await api.post(`/api/client-hiring/client-accounts/${accId}/users/${u.id}/reset-password`);
      if (r.data?.setup_url) navigator.clipboard?.writeText(r.data.setup_url).catch(() => {});
    },
    `Password reset link sent to ${u.email}. Copied to clipboard.`,
  );
  const onDeactivate = () => act(
    () => api.delete(`/api/client-hiring/client-accounts/${accId}/users/${u.id}`),
    `${u.name} deactivated.`,
  );

  return (
    <li className="flex items-center justify-between py-1.5 text-sm border-b last:border-0 border-zinc-100" data-testid={`cu-row-${u.id}`}>
      <div className="min-w-0 flex-1">
        <span className="text-zinc-900 font-medium">{u.name}</span>
        <span className="text-zinc-500 ml-2">{u.email}</span>
        <Badge variant="outline" className="ml-2 text-[10px]">{u.role.replace('client_', '').replaceAll('_', ' ')}</Badge>
        {!u.password_hash && u.magic_token_expires_at && <Badge className="ml-2 bg-amber-100 text-amber-800 text-[10px]">setup pending</Badge>}
      </div>
      <span className="text-xs text-zinc-400 mr-2 hidden sm:inline">
        {u.last_login_at ? `last seen ${new Date(u.last_login_at).toLocaleDateString()}` : "never logged in"}
      </span>
      <div className="relative">
        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setMenu((v) => !v)} data-testid={`cu-menu-${u.id}`}><DotsThree size={18} weight="bold" /></Button>
        {menu && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setMenu(false)} />
            <div className="absolute right-0 mt-1 w-56 bg-white border border-zinc-200 rounded-md shadow-lg z-20 py-1 text-sm" data-testid={`cu-menu-pop-${u.id}`}>
              <MenuItem icon={<PencilSimple size={14} />} onClick={() => { setMenu(false); setDialog("edit"); }} testid={`cu-edit-${u.id}`}>Edit name / role</MenuItem>
              <MenuItem icon={<ArrowsClockwise size={14} />} onClick={() => { setMenu(false); setDialog("resend-confirm"); }} testid={`cu-resend-${u.id}`}>Resend invite link</MenuItem>
              <MenuItem icon={<Key size={14} />} onClick={() => { setMenu(false); setDialog("set"); }} testid={`cu-setpw-${u.id}`}>Set password manually…</MenuItem>
              <MenuItem icon={<Key size={14} />} onClick={() => { setMenu(false); setDialog("reset-confirm"); }} testid={`cu-resetpw-${u.id}`}>Send reset-password link</MenuItem>
              <div className="border-t border-zinc-100 my-1" />
              <MenuItem icon={<Prohibit size={14} />} className="text-rose-700" onClick={() => { setMenu(false); setDialog("deactivate-confirm"); }} testid={`cu-deact-${u.id}`}>Deactivate user</MenuItem>
            </div>
          </>
        )}
      </div>

      {dialog === "edit" && <EditUserDialog u={u} accId={accId} onClose={close} onChanged={onChanged} />}
      {dialog === "set" && <SetPasswordDialog u={u} accId={accId} onClose={close} onChanged={onChanged} />}
      {dialog === "resend-confirm" && (
        <ConfirmDialog title="Re-send invite link?" desc={`A new 14-day setup link will be emailed to ${u.email} and copied to your clipboard.`} onClose={close} onConfirm={onResend} busy={busy} confirmLabel="Send invite" />
      )}
      {dialog === "reset-confirm" && (
        <ConfirmDialog title="Send password reset link?" desc={`This clears ${u.name}'s password and emails them a 14-day reset link.`} onClose={close} onConfirm={onReset} busy={busy} confirmLabel="Send reset link" tone="amber" />
      )}
      {dialog === "deactivate-confirm" && (
        <ConfirmDialog title="Deactivate this user?" desc={`${u.name} will lose access to the ${u.email.split('@')[1]} portal immediately. You can re-activate later.`} onClose={close} onConfirm={onDeactivate} busy={busy} confirmLabel="Deactivate" tone="rose" />
      )}
    </li>
  );
}

function MenuItem({ icon, children, onClick, testid, className = "" }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`w-full text-left px-3 py-1.5 flex items-center gap-2 hover:bg-zinc-50 ${className}`}>
      <span className="text-zinc-500">{icon}</span>{children}
    </button>
  );
}

function ConfirmDialog({ title, desc, onClose, onConfirm, busy, confirmLabel = "Confirm", tone = "indigo" }) {
  const btn = tone === "rose"
    ? "bg-rose-600 hover:bg-rose-700"
    : tone === "amber" ? "bg-amber-600 hover:bg-amber-700" : "bg-indigo-600 hover:bg-indigo-700";
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{desc}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className={btn} onClick={onConfirm} disabled={busy} data-testid="confirm-btn">
            {busy ? "Working…" : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditUserDialog({ u, accId, onClose, onChanged }) {
  const [name, setName] = useState(u.name || "");
  const [role, setRole] = useState(u.role || "client_hiring_manager");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/api/client-hiring/client-accounts/${accId}/users/${u.id}`, { name, role });
      toast.success("Updated"); onChanged(); onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Edit {u.email}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="cu-edit-name" />
          </div>
          <div>
            <Label className="text-xs">Role</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="cu-edit-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="client_admin">Admin — full control</SelectItem>
                <SelectItem value="client_hiring_manager">Hiring Manager — approve / interview</SelectItem>
                <SelectItem value="client_reviewer">Reviewer — read-only</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="text-[11px] text-zinc-500 italic">Email cannot be changed — that creates a fresh user.</div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="cu-edit-save">{busy ? "Saving…" : "Save changes"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SetPasswordDialog({ u, accId, onClose, onChanged }) {
  const [pwd, setPwd] = useState(() => suggestStrongPassword());
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (pwd.length < 8) { toast.error("Min 8 characters"); return; }
    setBusy(true);
    try {
      await api.post(`/api/client-hiring/client-accounts/${accId}/users/${u.id}/set-password`, { password: pwd, notify_user: notify });
      navigator.clipboard?.writeText(pwd).catch(() => {});
      toast.success(`Password set. ${notify ? "Emailed to user and " : ""}copied to clipboard.`);
      onChanged(); onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Set password for {u.name}</DialogTitle>
          <DialogDescription>For when {u.email.split('@')[1]} HR called and asked for credentials over the phone.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs">New password</Label>
            <div className="flex gap-2">
              <Input value={pwd} onChange={(e) => setPwd(e.target.value)} className="font-mono" data-testid="cu-set-pwd" />
              <Button type="button" variant="outline" onClick={() => setPwd(suggestStrongPassword())}>Re-gen</Button>
            </div>
            <div className="text-[11px] text-zinc-500 mt-1">Will be copied to your clipboard on submit.</div>
          </div>
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} data-testid="cu-set-notify" />
            Also email the credentials to {u.email}
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="cu-set-submit">
            {busy ? "Saving…" : "Set password"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function suggestStrongPassword() {
  const alphabet = "ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
  const symbols = "!@#$%&*+";
  let out = "";
  for (let i = 0; i < 11; i++) out += alphabet[Math.floor(Math.random() * alphabet.length)];
  out += symbols[Math.floor(Math.random() * symbols.length)];
  return out;
}
